'use client';

import React from 'react';
import type { DataTableColumn, DataTableRow } from './DataTableTypes';

export type DataTableStructureProps = {
  columns: DataTableColumn[];
  sortKey: string;
  sortDir: 'asc' | 'desc';
  onSort: (key: string) => void;
  visibleRows: DataTableRow[];
  getFilterRowCell?: (key: string) => { children: React.ReactNode; className?: string } | null;
  tableId: string;
  tableHelpId: string;
  tableClassName?: string;
  /**
   * Under `mobile-max`, adds class `table-mobile`: stacked row cards (`data-label` + SCSS) instead of a wide table.
   * Desktop unchanged. Pair with `DataTable` (`.table-sort-mobile`, `.table-wrap-mobile`).
   */
  mobileTableLayout?: boolean;
};

const DataTableStructure: React.FC<DataTableStructureProps> = ({
  columns,
  sortKey,
  sortDir,
  onSort,
  visibleRows,
  getFilterRowCell,
  tableId,
  tableHelpId,
  tableClassName,
  mobileTableLayout = true,
}) => {
  const tableClassNames = ['table', mobileTableLayout ? 'table-mobile' : null, tableClassName]
    .filter(Boolean)
    .join(' ');

  return (
    <table className={tableClassNames} aria-describedby={tableHelpId} data-table id={tableId}>
      {columns.length > 0 && (
        <thead>
          <tr>
            {columns.map(({ title, key, type }) => (
              <th
                key={key}
                className='th-sort'
                data-key={key}
                data-type={type}
                onClick={() => onSort(key)}
                data-dir={sortKey === key ? sortDir : undefined}
              >
                {title}
              </th>
            ))}
          </tr>
        </thead>
      )}
      <tbody>
        {getFilterRowCell && (
          <tr className='tr-filter'>
            {columns.map(({ key, title }) => {
              const spec = getFilterRowCell(key);
              if (spec) {
                return (
                  <td key={key} className={spec.className} data-label={title}>
                    {spec.children}
                  </td>
                );
              }
              return <td key={key} data-label={title}></td>;
            })}
          </tr>
        )}
        {visibleRows.map((row) => (
          <tr key={row.id}>
            {columns.map(({ key, title }) => (
              <td key={key} data-label={title}>
                {row[key]}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

export default DataTableStructure;
